import React, { useEffect, useMemo, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import feedbackIcon from '../../assets/feedback.png';
import profileGrey from '../../assets/profile_icon__grey-correct_.png';
import prairiLogo from '../../assets/prairiwhite.png';

export type InvestorActivityItem = {
  id: string;
  timestamp: string;
  text: string;
  thumbnailUrl?: string;
  companyName?: string;
  eventType: 'pitch_deck_updated' | 'startup_paused' | 'feedback_left' | 'joined';
};

const SURFACE = '#1d1d1d';

function formatActivityDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit' });
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function ThumbnailImg({ src, className }: { src: string; className: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <img src={profileGrey} alt="" className="h-full w-full object-contain p-2 opacity-70" />;
  }
  return (
    <img
      src={src}
      alt=""
      className={className}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function LandscapeThumb({
  thumbnailUrl,
  fallback,
}: {
  thumbnailUrl?: string;
  fallback?: React.ReactNode;
}) {
  return (
    <div className="relative z-[1] h-14 w-[5.5rem] shrink-0 overflow-hidden rounded-sm border-2 border-[#444444] bg-[#222] sm:h-[3.75rem] sm:w-24">
      {thumbnailUrl ? (
        <ThumbnailImg src={thumbnailUrl} className="h-full w-full object-cover" />
      ) : (
        fallback
      )}
    </div>
  );
}

/** Same width for every row so text lines up under Favorites. */
const THUMB_SLOT =
  'relative z-[1] flex h-14 w-[5.5rem] shrink-0 items-center justify-center sm:h-[3.75rem] sm:w-24';

function Thumbnail({ item }: { item: InvestorActivityItem }) {
  if (item.eventType === 'feedback_left') {
    // Startup thumb when available; otherwise bubble in the same-width slot
    if (item.thumbnailUrl) {
      return <LandscapeThumb thumbnailUrl={item.thumbnailUrl} fallback={null} />;
    }
    return (
      <div className={THUMB_SLOT}>
        <img src={feedbackIcon} alt="" className="h-9 w-9 object-contain" />
      </div>
    );
  }

  if (item.eventType === 'pitch_deck_updated' || item.eventType === 'startup_paused') {
    return (
      <LandscapeThumb
        thumbnailUrl={item.thumbnailUrl}
        fallback={
          <img src={profileGrey} alt="" className="h-full w-full object-contain p-2 opacity-70" />
        }
      />
    );
  }

  // Joined — PRAIRI mark, same slot width as landscape thumbs
  return (
    <div className={THUMB_SLOT}>
      <div className="relative h-14 w-[5.5rem] overflow-hidden rounded-sm border-2 border-[#444444] bg-[#222] sm:h-[3.75rem] sm:w-24">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <img
            src={prairiLogo}
            alt=""
            className="h-6 w-auto object-contain mix-blend-lighten sm:h-7"
          />
          <span className="text-[9px] font-semibold leading-none tracking-[0.28em] text-white sm:text-[10px]">
            PRAIRI
          </span>
        </div>
      </div>
    </div>
  );
}

function ActivityText({ item }: { item: InvestorActivityItem }) {
  const name = item.companyName?.trim();

  if (item.eventType === 'joined') {
    return (
      <p className="text-sm leading-snug text-white sm:text-base">
        You joined <span className="font-bold">PRAIRI</span>
      </p>
    );
  }

  if (item.eventType === 'feedback_left') {
    return (
      <p className="text-sm leading-snug text-white sm:text-base">
        You left feedback for {name ? <span className="font-bold">{name}</span> : 'a startup'}
      </p>
    );
  }

  if (item.eventType === 'pitch_deck_updated' && name) {
    return (
      <p className="text-sm leading-snug text-white sm:text-base">
        <span className="font-bold">{name}</span> updated their pitch deck
      </p>
    );
  }

  if (item.eventType === 'startup_paused' && name) {
    return (
      <p className="text-sm leading-snug text-white sm:text-base">
        <span className="font-bold">{name}</span> paused their profile since they received their
        funding or another reason
      </p>
    );
  }

  return <p className="text-sm leading-snug text-white sm:text-base">{item.text}</p>;
}

const InvestorActivityPanel: React.FC = () => {
  const [items, setItems] = useState<InvestorActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const getInvestorDashboardActivity = httpsCallable(
          getFunctions(),
          'getInvestorDashboardActivity'
        );
        const result = await getInvestorDashboardActivity({});
        const data = result.data as {
          success?: boolean;
          items?: InvestorActivityItem[];
          error?: string;
        };
        if (cancelled) return;
        if (data?.success && Array.isArray(data.items)) {
          setItems(data.items);
        } else {
          setItems([]);
          setError(data?.error || 'Could not load activity');
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setError(err instanceof Error ? err.message : 'Could not load activity');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: InvestorActivityItem[] }>();
    for (const item of items) {
      const d = new Date(item.timestamp);
      const key = dateKey(d);
      const existing = map.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        map.set(key, { label: formatActivityDate(d), items: [item] });
      }
    }
    return Array.from(map.values());
  }, [items]);

  return (
    <div
      className="flex-1 min-h-0 overflow-y-auto prairi-hide-native-scrollbar px-4 pb-8 pt-2 text-left lg:pb-6 lg:pt-12 lg:pl-24 xl:pl-32"
      style={{ backgroundColor: SURFACE }}
    >
      {loading ? (
        <p className="text-sm text-gray-400">Loading activity…</p>
      ) : error && items.length === 0 ? (
        <p className="text-sm text-gray-400">{error}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">No activity yet.</p>
      ) : (
        <div className="max-w-4xl space-y-8 lg:space-y-10">
          {groups.map(group => (
            <section key={group.label}>
              <p className="mb-3 text-left text-sm leading-snug text-gray-400 lg:hidden">
                {group.label}
              </p>
              <div className="lg:grid lg:grid-cols-[8.5rem_1fr] lg:items-start lg:gap-x-20">
                <p className="hidden pt-3 pr-1 text-left text-sm leading-snug text-gray-400 lg:block">
                  {group.label}
                </p>
                <ul className="min-w-0 space-y-5 lg:space-y-6">
                  {group.items.map(item => (
                    <li key={item.id} className="flex items-start gap-3 lg:items-center lg:gap-4">
                      <Thumbnail item={item} />
                      <div className="min-w-0 flex-1">
                        <ActivityText item={item} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvestorActivityPanel;
